import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserTag, faPlus, faCheck, faSave, faTrash, faEdit, faStickyNote } from '@fortawesome/free-solid-svg-icons';
import AppContext from 'context/Context';
import { useToast } from 'context/providers/ToastProvider';
import FalconCloseButton from 'components/common/FalconCloseButton';
export const CLIENT_COLOR_PRESETS = [
  {
    id: 'green',
    name: 'Verde',
    hex: '#d1fae5',
    lightBg: '#d1fae5',
    lightBorder: '#a7f3d0',
    lightText: '#065f46',
    darkBg: 'rgba(16, 185, 129, 0.15)',
    darkBorder: 'rgba(16, 185, 129, 0.35)',
    darkText: '#34d399'
  },
  {
    id: 'blue',
    name: 'Azul',
    hex: '#dbeafe',
    lightBg: '#dbeafe',
    lightBorder: '#bfdbfe',
    lightText: '#1e40af',
    darkBg: 'rgba(59, 130, 246, 0.15)',
    darkBorder: 'rgba(59, 130, 246, 0.35)',
    darkText: '#60a5fa'
  },
  {
    id: 'purple',
    name: 'Morado',
    hex: '#f3e8ff',
    lightBg: '#f3e8ff',
    lightBorder: '#e9d5ff',
    lightText: '#6b21a8',
    darkBg: 'rgba(168, 85, 247, 0.15)',
    darkBorder: 'rgba(168, 85, 247, 0.35)',
    darkText: '#c084fc'
  },
  {
    id: 'orange',
    name: 'Naranja',
    hex: '#ffedd5',
    lightBg: '#ffedd5',
    lightBorder: '#fed7aa',
    lightText: '#9a3412',
    darkBg: 'rgba(249, 115, 22, 0.15)',
    darkBorder: 'rgba(249, 115, 22, 0.35)',
    darkText: '#fb923c'
  },
  {
    id: 'yellow',
    name: 'Amarillo',
    hex: '#fef3c7',
    lightBg: '#fef3c7',
    lightBorder: '#fde68a',
    lightText: '#92400e',
    darkBg: 'rgba(245, 158, 11, 0.15)',
    darkBorder: 'rgba(245, 158, 11, 0.35)',
    darkText: '#fbbf24'
  }
];

export const getClientNoteColorStyle = (colorValue, isDark = false) => {
  const normalized = (colorValue || '').toLowerCase().trim();
  const preset = CLIENT_COLOR_PRESETS.find(
    c => c.hex.toLowerCase() === normalized || c.id === normalized
  );

  if (preset) {
    return isDark ? {
      backgroundColor: preset.darkBg,
      borderColor: preset.darkBorder,
      color: preset.darkText
    } : {
      backgroundColor: preset.lightBg,
      borderColor: preset.lightBorder,
      color: preset.lightText
    };
  }

  const defaultPreset = CLIENT_COLOR_PRESETS[0];
  return isDark ? {
    backgroundColor: defaultPreset.darkBg,
    borderColor: defaultPreset.darkBorder,
    color: defaultPreset.darkText
  } : {
    backgroundColor: defaultPreset.lightBg,
    borderColor: defaultPreset.lightBorder,
    color: defaultPreset.lightText
  };
};

const ClientNotesModal = ({
  show,
  onClose,
  customerId,
  customerName,
  notes = [],
  isLoadingNotes = false,
  isSavingNote = false,
  initialEditingNote = null,
  createNote,
  updateNote,
  deleteNote
}) => {
  const { config: { isDark } = {} } = useContext(AppContext);
  const { showSuccessToast, showErrorToast } = useToast();

  const [noteText, setNoteText] = useState('');
  const [selectedColor, setSelectedColor] = useState(CLIENT_COLOR_PRESETS[0].hex);
  const [editingNote, setEditingNote] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  useEffect(() => {
    if (show) {
      if (initialEditingNote) {
        setEditingNote(initialEditingNote);
        setNoteText(initialEditingNote.note || '');
        setSelectedColor(initialEditingNote.noteColor || CLIENT_COLOR_PRESETS[0].hex);
      } else {
        setEditingNote(null);
        setNoteText('');
        setSelectedColor(CLIENT_COLOR_PRESETS[0].hex);
      }
    } else {
      setEditingNote(null);
      setNoteText('');
    }
  }, [show, initialEditingNote]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!noteText.trim()) return;

    try {
      if (editingNote) {
        if (updateNote) {
          await updateNote({
            idClienteNotas: editingNote.idClienteNotas,
            note: noteText.trim(),
            noteColor: selectedColor
          });
          showSuccessToast('Nota de cliente actualizada');
        }
      } else {
        if (createNote) {
          await createNote({
            note: noteText.trim(),
            noteColor: selectedColor
          });
          showSuccessToast('Nota de cliente agregada');
        }
      }
      setNoteText('');
      setEditingNote(null);
      setSelectedColor(CLIENT_COLOR_PRESETS[0].hex);
    } catch (err) {
      showErrorToast(err.message || 'Error al guardar la nota');
    }
  };

  const handleDelete = async (noteId) => {
    try {
      if (deleteNote) {
        await deleteNote({ idClienteNotas: noteId });
        showSuccessToast('Nota de cliente eliminada');
      }
      setConfirmDeleteId(null);
    } catch (err) {
      showErrorToast(err.message || 'Error al eliminar la nota');
    }
  };

  return (
    <Modal
      show={show}
      onHide={() => {
        setEditingNote(null);
        setConfirmDeleteId(null);
        onClose();
      }}
      centered
      size="lg"
      contentClassName={isDark ? 'bg-card-dark text-white' : 'bg-white'}
    >
      <Modal.Header className="d-flex align-items-center justify-content-between border-bottom pb-2">
        <div className="d-flex align-items-center gap-2">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center"
            style={{
              width: '36px',
              height: '36px',
              backgroundColor: isDark ? 'rgba(52, 211, 153, 0.2)' : '#dcfce7',
              color: isDark ? '#34d399' : '#059669'
            }}
          >
            <FontAwesomeIcon icon={faUserTag} />
          </div>
          <div>
            <h6 className="mb-0 fw-bold" style={{ fontSize: '1rem', color: isDark ? '#f9fafd' : '#0f172a' }}>
              Notas del Cliente #{customerId}
            </h6>
            <small className="text-muted" style={{ fontSize: '0.75rem' }}>
              {customerName ? `${customerName} • ` : ''}Visible para todo el equipo en todos sus tickets
            </small>
          </div>
        </div>
        <FalconCloseButton
          onClick={() => {
            setEditingNote(null);
            setConfirmDeleteId(null);
            onClose();
          }}
        />
      </Modal.Header>

      <Modal.Body className="p-3">
        {/* Formulario de creación / edición */}
        <Form onSubmit={handleSubmit} className="mb-3 p-3 rounded-3 border" style={{ backgroundColor: isDark ? '#182635' : '#f8fafc' }}>
          <div className="d-flex align-items-center justify-content-between mb-2">
            <span className="fw-semibold small" style={{ color: isDark ? '#e2e8f0' : '#334155' }}>
              {editingNote ? 'Editar nota del cliente' : 'Nueva nota del cliente'}
            </span>
            {editingNote && (
              <Button
                variant="link"
                size="sm"
                className="p-0 text-muted"
                onClick={() => setEditingNote(null)}
              >
                Cancelar edición
              </Button>
            )}
          </div>

          <Form.Group className="mb-2">
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Escribe una nota para identificar a este cliente (ej. Cuenta VIP, llamar solo en la tarde, software especializado)..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              style={{
                backgroundColor: isDark ? '#121e2d' : '#ffffff',
                color: isDark ? '#f9fafd' : '#0f172a',
                borderColor: isDark ? '#233142' : '#cbd5e1',
                fontSize: '0.88rem'
              }}
              autoFocus
            />
          </Form.Group>

          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            {/* Color Presets */}
            <div className="d-flex align-items-center gap-1">
              <span className="small text-muted me-1" style={{ fontSize: '0.75rem' }}>Color:</span>
              {CLIENT_COLOR_PRESETS.map((color) => {
                const isSelected = (selectedColor || '').toLowerCase() === color.hex.toLowerCase();
                return (
                  <button
                    key={color.id}
                    type="button"
                    className="rounded-circle border-0 d-flex align-items-center justify-content-center"
                    style={{
                      width: '24px',
                      height: '24px',
                      backgroundColor: color.hex,
                      cursor: 'pointer',
                      outline: isSelected ? `2px solid ${isDark ? '#34d399' : '#059669'}` : 'none',
                      outlineOffset: '2px'
                    }}
                    title={color.name}
                    onClick={() => setSelectedColor(color.hex)}
                  >
                    {isSelected && (
                      <FontAwesomeIcon
                        icon={faCheck}
                        style={{ fontSize: '0.65rem', color: color.lightText }}
                      />
                    )}
                  </button>
                );
              })}
            </div>

            <Button
              type="submit"
              size="sm"
              disabled={isSavingNote || !noteText.trim()}
              className="d-flex align-items-center gap-2 px-3 py-1"
              style={{ backgroundColor: '#059669', borderColor: '#059669' }}
            >
              {isSavingNote ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <FontAwesomeIcon icon={editingNote ? faSave : faPlus} />
                  <span>{editingNote ? 'Actualizar' : 'Guardar Nota'}</span>
                </>
              )}
            </Button>
          </div>
        </Form>

        {/* Lista de notas registradas */}
        <div>
          <h6 className="fw-semibold small text-muted mb-2 text-uppercase" style={{ fontSize: '0.75rem' }}>
            Historial de notas ({notes.length})
          </h6>

          {isLoadingNotes ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" className="text-primary me-2" />
              <span className="small text-muted">Cargando notas del cliente...</span>
            </div>
          ) : notes.length === 0 ? (
            <div className="text-center py-4 border rounded-3 text-muted small" style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }}>
              <FontAwesomeIcon icon={faStickyNote} className="mb-2 d-block mx-auto" style={{ fontSize: '1.5rem', opacity: 0.4 }} />
              Este cliente no tiene notas registradas aún.
            </div>
          ) : (
            <div className="d-flex flex-column gap-2" style={{ maxHeight: '320px', overflowY: 'auto' }}>
              {notes.map((noteItem) => {
                const colorStyle = getClientNoteColorStyle(noteItem.noteColor, isDark);
                const isDeleting = confirmDeleteId === noteItem.idClienteNotas;

                return (
                  <div
                    key={noteItem.idClienteNotas}
                    className="p-3 rounded-3 position-relative border"
                    style={{
                      ...colorStyle,
                      borderLeftWidth: '4px',
                      borderLeftColor: colorStyle.color || colorStyle.borderColor
                    }}
                  >
                    <div className="d-flex align-items-start justify-content-between gap-2">
                      <div className="flex-grow-1">
                        <p className="mb-1" style={{ fontSize: '0.88rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {noteItem.note}
                        </p>
                        <div className="d-flex align-items-center gap-2 text-muted" style={{ fontSize: '0.72rem' }}>
                          <span className="fw-semibold">{noteItem.createdUser || 'Asesor'}</span>
                          <span>•</span>
                          <span>{new Date(noteItem.createdDate).toLocaleDateString()} {new Date(noteItem.createdDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      </div>

                      {/* Acciones Editar / Eliminar */}
                      <div className="d-flex align-items-center gap-1 flex-shrink-0">
                        {isDeleting ? (
                          <div className="d-flex align-items-center gap-1 bg-white p-1 rounded border shadow-sm">
                            <span className="text-danger small" style={{ fontSize: '0.72rem' }}>¿Borrar?</span>
                            <Button
                              variant="danger"
                              size="sm"
                              className="py-0 px-1 text-white"
                              style={{ fontSize: '0.70rem' }}
                              onClick={() => handleDelete(noteItem.idClienteNotas)}
                            >
                              Sí
                            </Button>
                            <Button
                              variant="secondary"
                              size="sm"
                              className="py-0 px-1"
                              style={{ fontSize: '0.70rem' }}
                              onClick={() => setConfirmDeleteId(null)}
                            >
                              No
                            </Button>
                          </div>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="btn btn-sm btn-link p-1 text-muted hover-text-primary"
                              title="Editar nota"
                              onClick={() => setEditingNote(noteItem)}
                            >
                              <FontAwesomeIcon icon={faEdit} style={{ fontSize: '0.8rem' }} />
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-link p-1 text-muted hover-text-danger"
                              title="Eliminar nota"
                              onClick={() => setConfirmDeleteId(noteItem.idClienteNotas)}
                            >
                              <FontAwesomeIcon icon={faTrash} style={{ fontSize: '0.8rem' }} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

ClientNotesModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  customerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  customerName: PropTypes.string,
  notes: PropTypes.array,
  isLoadingNotes: PropTypes.bool,
  isSavingNote: PropTypes.bool,
  initialEditingNote: PropTypes.object,
  createNote: PropTypes.func,
  updateNote: PropTypes.func,
  deleteNote: PropTypes.func
};

export default ClientNotesModal;
